import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  getData,
  getDataSuperAdmin,
  postData,
  postDataSuperAdmin,
  updateData,
  updateDataSuperAdmin,
} from "../../../redux/actions/common.action";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Text,
  useToast,
  VStack,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { ArrowLeft, ChevronRight, Home, Save } from "lucide-react";
import { MdSecurity } from "react-icons/md";
import { AiFillSecurityScan } from "react-icons/ai";

type TermsForm = {
  title: string;
  terms: string;
  category: string;
};

interface TermsCategory {
  _id: string;
  name: string;
}

interface TermsState {
  termsCategories: TermsCategory[];
  loading: boolean;
  signinuser: {
    UserType: number;
  };
  superTermsCategories: {
    _id: string;
    name: string;
  }[];
}

const TermsEditor = () => {
  const location = useLocation();
  const templateData = location.state?.template;

  const {
    loading,
    termsCategories = [],
    signinuser,
    superTermsCategories,
  } = useSelector((state: { common: TermsState }) => state.common);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TermsForm>();

  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");

  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  const filteredTermsCategories =
    signinuser.UserType === 1
      ? superTermsCategories
      : signinuser.UserType !== 1
      ? termsCategories
      : null;

  useEffect(() => {
    if (termsCategories?.length > 0) return;
    dispatch(getData("termsCategory"));
  }, [dispatch]);

  const onSubmit = (data: TermsForm) => {
    if (editId) {
      if (signinuser?.UserType !== 1) {
        dispatch(
          updateData(editId, "proposalTerms", data, navigate, toast)
        ).then(() => {
          dispatch(getData("proposalTerms"));
        });
      } else if (signinuser?.UserType === 1) {
        dispatch(
          updateDataSuperAdmin(
            editId,
            "superProposalTerms",
            data,
            navigate,
            toast
          )
        );
      }
    } else {
      if (signinuser?.UserType !== 1) {
        dispatch(postData(data, "proposalTerms", navigate, toast)).then(() => {
          dispatch(getData("proposalTerms"));
        });
      } else if (signinuser?.UserType === 1) {
        dispatch(
          postDataSuperAdmin(data, "proposalTerms", navigate, toast)
        ).then(() => {
          dispatch(getDataSuperAdmin("superTermsCategories"));
        });
      }
    }
    reset();
  };

  // Populate if editing
  useEffect(() => {
    if (templateData?.category) {
      setValue("category", templateData?.category);
      setValue("title", templateData?.title);
      setValue("terms", templateData?.terms);
    }
  }, [templateData, setValue]);

  const handleBack = () => {
    navigate("/proposal-terms");
  };

  const handleBackToProposal = () => {
    navigate("/proposal-list");
  };

  return (
    <>
      {/* Modern Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p={4}
        mx={{ base: 2, md: 4 }}
        mt={4}
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <VStack align="start" spacing={2}>
            <Button
              leftIcon={<ArrowLeft size={16} />}
              onClick={handleBack}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              fontWeight="medium"
              fontSize="sm"
              px={3}
              _hover={{
                bg: "blue.50",
                transform: "translateX(-4px)",
              }}
              transition="all 0.2s"
            >
              <Box display={{ base: "none", sm: "inline" }}>Back to Terms</Box>
            </Button>

            <Breadcrumb
              spacing={2}
              separator={<ChevronRight size={12} color="#9CA3AF" />}
              fontSize="xs"
              color="gray.600"
              display={{ base: "none", md: "inline" }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={handleBackToProposal}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <Home size={12} />
                  <Text>Proposals</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={handleBack}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <MdSecurity size={12} />
                  <Text>Terms</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <AiFillSecurityScan size={12} />
                  <Text>{editId ? "Edit" : "Create"} Template</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </VStack>

          <HStack spacing={4}>
            <VStack align="end" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {editId ? "Edit Terms" : "Create New Terms"}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {editId ? "Update terms template" : "Add new terms template"}
              </Text>
            </VStack>
            <Box
              bgGradient="linear(to-br, purple.400, purple.600)"
              p={3}
              borderRadius="xl"
              boxShadow="md"
            >
              <AiFillSecurityScan size={20} color="white" />
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Editor Form */}
      <Box
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        bg="white"
        p={{ base: 3, md: 6 }}
        rounded="xl"
        shadow="sm"
        border="1px"
        borderColor="gray.200"
        mb={6}
        mx={{ base: 2, md: 4 }}
      >
        <VStack spacing={6} align="stretch">
          {/* Category & Title Row */}
          <VStack spacing={4} align="stretch">
            <Heading
              size="md"
              color="gray.800"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Box
                bg="purple.100"
                p={2}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <AiFillSecurityScan size={18} color="#9333EA" />
              </Box>
              Template Details
            </Heading>

            <Flex
              gap={4}
              direction={{ base: "column", md: "row" }}
              align="flex-start"
            >
              {/* Category Select */}
              <FormControl isInvalid={!!errors.category} flex={1}>
                <FormLabel
                  fontSize="sm"
                  fontWeight="600"
                  color="gray.700"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                >
                  Category
                </FormLabel>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: "Category is required" }}
                  render={({ field }) => (
                    <Select
                      placeholder="Select category"
                      borderRadius="lg"
                      border="2px"
                      borderColor="gray.200"
                      fontSize="sm"
                      fontWeight="500"
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 3px rgba(147, 51, 234, 0.1)",
                      }}
                      _hover={{
                        borderColor: "gray.300",
                      }}
                      {...field}
                    >
                      {filteredTermsCategories?.map((cat: any) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                {errors.category && (
                  <Text color="red.500" fontSize="xs" mt={1} fontWeight="500">
                    {errors.category.message}
                  </Text>
                )}
              </FormControl>

              {/* Title Input */}
              <FormControl isInvalid={!!errors.title} flex={1}>
                <FormLabel
                  fontSize="sm"
                  fontWeight="600"
                  color="gray.700"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                >
                  Title
                </FormLabel>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <Input
                      placeholder="Enter terms title"
                      borderRadius="lg"
                      border="2px"
                      borderColor="gray.200"
                      fontSize="sm"
                      fontWeight="500"
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 3px rgba(147, 51, 234, 0.1)",
                      }}
                      _hover={{
                        borderColor: "gray.300",
                      }}
                      {...field}
                    />
                  )}
                />
                {errors.title && (
                  <Text color="red.500" fontSize="xs" mt={1} fontWeight="500">
                    {errors.title.message}
                  </Text>
                )}
              </FormControl>
            </Flex>
          </VStack>

          {/* Editor Section */}
          <VStack spacing={4} align="stretch">
            <Heading
              size="md"
              color="gray.800"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Box
                bg="purple.100"
                p={2}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <AiFillSecurityScan size={18} color="#9333EA" />
              </Box>
              Content
            </Heading>

            <FormControl isInvalid={!!errors.terms}>
              <FormLabel
                fontSize="sm"
                fontWeight="600"
                color="gray.700"
                mb={2}
                textTransform="uppercase"
                letterSpacing="0.5px"
              >
                Terms Body
              </FormLabel>
              <Controller
                name="terms"
                control={control}
                rules={{
                  required: "Body is required",
                  validate: (value) =>
                    value?.replace(/<[^>]*>/g, "").trim().length > 0 ||
                    "Body content cannot be empty",
                }}
                render={({ field: { onChange, value } }) => (
                  <Box>
                    <Box
                      border="2px"
                      borderColor={errors?.terms ? "red.500" : "gray.200"}
                      borderRadius="lg"
                      p={4}
                      bg="gray.50"
                      transition="all 0.2s"
                      _hover={{
                        borderColor: "gray.300",
                        bg: "white",
                      }}
                      _focusWithin={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 3px rgba(147, 51, 234, 0.1)",
                        bg: "white",
                      }}
                    >
                      <CKEditor
                        editor={ClassicEditor}
                        data={value || ""}
                        onChange={(event: any, editor: any) => {
                          const data = editor.getData();
                          onChange(data);
                        }}
                        onReady={(editor: any) => {
                          editor.editing.view.change((writer: any) => {
                            writer.setStyle(
                              "min-height",
                              "250px",
                              editor.editing.view.document.getRoot()
                            );
                          });
                        }}
                      />
                    </Box>
                    {errors?.terms && (
                      <Text
                        color="red.500"
                        fontSize="sm"
                        mt={2}
                        fontWeight="500"
                      >
                        {errors.terms.message}
                      </Text>
                    )}
                  </Box>
                )}
              />
            </FormControl>
          </VStack>

          {/* Footer Actions */}
          <Box
            borderTop="2px"
            borderColor="gray.100"
            pt={6}
            bg="gray.50"
            mx={-6}
            px={6}
            py={4}
            borderBottomRadius="xl"
            mt={8}
          >
            <HStack
              spacing={3}
              justify="flex-end"
              direction={{ base: "column-reverse", sm: "row" }}
            >
              <Button
                variant="ghost"
                colorScheme="gray"
                size="md"
                fontSize="sm"
                fontWeight="600"
                h="44px"
                borderRadius="lg"
                onClick={handleBack}
                px={6}
                _hover={{
                  bg: "gray.200",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="md"
                fontSize="sm"
                fontWeight="700"
                h="44px"
                borderRadius="lg"
                px={6}
                bgGradient="linear(to-br, purple.400, purple.600)"
                color="white"
                leftIcon={<Save size={16} />}
                boxShadow="0 4px 12px -2px rgba(147, 51, 234, 0.3)"
                isLoading={loading}
                loadingText={editId ? "Updating..." : "Saving..."}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                _hover={{
                  bgGradient: "linear(to-br, purple.500, purple.700)",
                  boxShadow: "0 8px 16px -4px rgba(147, 51, 234, 0.4)",
                  transform: "translateY(-1px)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
              >
                {editId ? "Update" : "Save"} Terms
              </Button>
            </HStack>
          </Box>
        </VStack>
      </Box>
    </>
  );
};

export default TermsEditor;
